import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';

// ─── Constants ────────────────────────────────────────────────────────────────
const CLASS_COLORS = {
  car:        '#00e5ff',
  pedestrian: '#76ff03',
  bike:       '#ff6d00',
  bus:        '#d500f9',
  truck:      '#ffea00',
};

const CLASS_LABELS = {
  car: 'Car', pedestrian: 'Pedestrian', bike: 'Bike', bus: 'Bus', truck: 'Truck',
};

const MAX_TREND_POINTS = 40;
const WS_URL = 'ws://localhost:8765';

// ─── Utility ──────────────────────────────────────────────────────────────────
function fmt(n) { return n.toString().padStart(2, '0'); }
function timestamp() {
  const d = new Date();
  return `${fmt(d.getHours())}:${fmt(d.getMinutes())}:${fmt(d.getSeconds())}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusDot({ connected }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: connected ? '#76ff03' : '#ff1744',
        boxShadow: connected ? '0 0 8px #76ff03' : '0 0 8px #ff1744',
        display: 'inline-block',
        animation: connected ? 'pulse 2s infinite' : 'none',
      }} />
      <span style={{ fontSize: 11, color: connected ? '#76ff03' : '#ff1744', letterSpacing: 1 }}>
        {connected ? 'LIVE' : 'OFFLINE'}
      </span>
    </span>
  );
}

function MetricCard({ label, value, unit = '', color = '#00e5ff', icon }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${color}22`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 8,
      padding: '14px 18px',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ fontSize: 10, color: '#8899aa', letterSpacing: 2, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
        {value}<span style={{ fontSize: 13, marginLeft: 4, color: `${color}99` }}>{unit}</span>
      </div>
      {icon && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 22, opacity: 0.15 }}>{icon}</div>}
    </div>
  );
}

function EventLog({ events }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [events]);

  return (
    <div style={{
      background: 'rgba(0,0,0,0.4)',
      border: '1px solid rgba(0,229,255,0.12)',
      borderRadius: 8,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid rgba(0,229,255,0.1)',
        fontSize: 10,
        letterSpacing: 2,
        color: '#8899aa',
        textTransform: 'uppercase',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>⚡ Event Feed</span>
        <span style={{ color: '#00e5ff55' }}>{events.length} events</span>
      </div>
      <div ref={ref} style={{
        overflowY: 'auto',
        flex: 1,
        padding: '8px 0',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
      }}>
        {events.length === 0 && (
          <div style={{ color: '#445566', padding: '20px 16px', textAlign: 'center' }}>
            Waiting for detections...
          </div>
        )}
        {events.map((ev, i) => (
          <div key={ev.id} style={{
            padding: '5px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.03)',
            display: 'flex',
            gap: 10,
            alignItems: 'baseline',
            animation: i === events.length - 1 ? 'fadeIn 0.3s ease' : 'none',
          }}>
            <span style={{ color: '#445566', flexShrink: 0 }}>{ev.time}</span>
            <span style={{ color: CLASS_COLORS[ev.cls] || '#00e5ff', flexShrink: 0 }}>
              [{CLASS_LABELS[ev.cls] || ev.cls}]
            </span>
            <span style={{ color: '#8899aa' }}>{ev.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideoFeed({ frameUrl, connected }) {
  return (
    <div style={{
      background: '#000',
      border: '1px solid rgba(0,229,255,0.15)',
      borderRadius: 8,
      overflow: 'hidden',
      position: 'relative',
      aspectRatio: '16/9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {frameUrl ? (
        <img
          src={frameUrl}
          alt="Live detection feed"
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          color: '#334455',
        }}>
          <div style={{ fontSize: 40 }}>📹</div>
          <div style={{ fontSize: 12, letterSpacing: 2 }}>
            {connected ? 'AWAITING STREAM...' : 'CONNECT COLAB BACKEND'}
          </div>
        </div>
      )}
      {/* Corner decorations */}
      {['tl','tr','bl','br'].map(c => (
        <div key={c} style={{
          position: 'absolute',
          width: 16, height: 16,
          borderColor: '#00e5ff55',
          borderStyle: 'solid',
          borderWidth: 0,
          ...(c === 'tl' ? { top: 8, left: 8, borderTopWidth: 2, borderLeftWidth: 2 } :
              c === 'tr' ? { top: 8, right: 8, borderTopWidth: 2, borderRightWidth: 2 } :
              c === 'bl' ? { bottom: 8, left: 8, borderBottomWidth: 2, borderLeftWidth: 2 } :
                           { bottom: 8, right: 8, borderBottomWidth: 2, borderRightWidth: 2 }),
        }} />
      ))}
      <div style={{
        position: 'absolute', top: 10, left: 10,
        background: 'rgba(0,0,0,0.7)',
        border: '1px solid rgba(0,229,255,0.2)',
        borderRadius: 4,
        padding: '3px 8px',
        fontSize: 9,
        letterSpacing: 2,
        color: frameUrl ? '#76ff03' : '#ff1744',
      }}>
        {frameUrl ? '● REC' : '○ NO SIGNAL'}
      </div>
    </div>
  );
}

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(8,16,26,0.95)',
      border: '1px solid rgba(0,229,255,0.2)',
      borderRadius: 6,
      padding: '8px 12px',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 11,
    }}>
      <div style={{ color: '#8899aa', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

// ─── Mock data generator (when no WS connection) ──────────────────────────────
function generateMockCounts() {
  return {
    car: Math.floor(Math.random() * 18 + 5),
    pedestrian: Math.floor(Math.random() * 8),
    bike: Math.floor(Math.random() * 5),
    bus: Math.floor(Math.random() * 3),
    truck: Math.floor(Math.random() * 4),
  };
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [connected, setConnected] = useState(false);
  const [useMock, setUseMock] = useState(false);
  const [counts, setCounts] = useState({ car: 0, pedestrian: 0, bike: 0, bus: 0, truck: 0 });
  const [trend, setTrend] = useState([]);
  const [events, setEvents] = useState([]);
  const [frameUrl, setFrameUrl] = useState(null);
  const [peakTotal, setPeakTotal] = useState(0);
  const [fps, setFps] = useState(0);
  const [sessionTime, setSessionTime] = useState(0);

  const wsRef = useRef(null);
  const mockIntervalRef = useRef(null);
  const eventIdRef = useRef(0);
  const prevCountsRef = useRef({});

  // Session timer
  useEffect(() => {
    const t = setInterval(() => setSessionTime(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const addEvent = useCallback((cls, count) => {
    const prev = prevCountsRef.current[cls] || 0;
    if (count > prev) {
      const diff = count - prev;
      const msg = diff > 2
        ? `High density — ${count} detected (+${diff})`
        : `${count} in frame`;
      setEvents(ev => {
        const next = [...ev, {
          id: eventIdRef.current++,
          time: timestamp(),
          cls,
          msg,
        }];
        return next.slice(-120); // keep last 120
      });
    }
    prevCountsRef.current[cls] = count;
  }, []);

  const processCounts = useCallback((newCounts) => {
    setCounts(newCounts);
    const total = Object.values(newCounts).reduce((a, b) => a + b, 0);
    setPeakTotal(p => Math.max(p, total));

    setTrend(t => {
      const next = [...t, { time: timestamp(), total, ...newCounts }];
      return next.slice(-MAX_TREND_POINTS);
    });

    Object.entries(newCounts).forEach(([cls, count]) => addEvent(cls, count));
  }, [addEvent]);

  // WebSocket connection to Colab backend
  useEffect(() => {
    if (useMock) return;

    let ws;
    let fpsCounter = 0;
    let fpsTimer;

    const connect = () => {
      try {
        ws = new WebSocket(WS_URL);
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          fpsTimer = setInterval(() => {
            setFps(fpsCounter);
            fpsCounter = 0;
          }, 1000);
        };

        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);

            if (data.frame) {
              setFrameUrl(`data:image/jpeg;base64,${data.frame}`);
              fpsCounter++;
            }

            if (data.counts) {
              processCounts(data.counts);
            }
          } catch (_) {}
        };

        ws.onclose = () => {
          setConnected(false);
          clearInterval(fpsTimer);
          setTimeout(connect, 3000); // auto-reconnect
        };

        ws.onerror = () => ws.close();
      } catch (_) {
        setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      ws?.close();
      clearInterval(fpsTimer);
    };
  }, [useMock, processCounts]);

  // Mock mode
  useEffect(() => {
    if (!useMock) {
      clearInterval(mockIntervalRef.current);
      return;
    }
    setConnected(false);
    mockIntervalRef.current = setInterval(() => {
      processCounts(generateMockCounts());
      setFps(12 + Math.floor(Math.random() * 6));
    }, 800);
    return () => clearInterval(mockIntervalRef.current);
  }, [useMock, processCounts]);

  const totalNow = Object.values(counts).reduce((a, b) => a + b, 0);

  const pieData = Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: CLASS_LABELS[k], value: v, color: CLASS_COLORS[k] }));

  const barData = Object.entries(counts).map(([k, v]) => ({
    name: CLASS_LABELS[k],
    count: v,
    fill: CLASS_COLORS[k],
  }));

  const sessionFmt = `${fmt(Math.floor(sessionTime / 60))}:${fmt(sessionTime % 60)}`;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&family=Bebas+Neue&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #05080f; color: #ccd6e8; font-family: 'DM Sans', sans-serif; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1a2a3a; border-radius: 2px; }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
      `}</style>

      {/* Scanline overlay for atmosphere */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 999,
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
      }} />

      <div style={{ minHeight: '100vh', padding: '16px 20px', maxWidth: 1600, margin: '0 auto' }}>

        {/* ── Header ── */}
        <header style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid rgba(0,229,255,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
            <h1 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 28,
              letterSpacing: 4,
              color: '#00e5ff',
              textShadow: '0 0 20px rgba(0,229,255,0.4)',
            }}>
              TRAFFIQ
            </h1>
            <span style={{ fontSize: 11, color: '#445566', letterSpacing: 2 }}>
              INTELLIGENCE COMMAND CENTER
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <button
              onClick={() => setUseMock(m => !m)}
              style={{
                background: useMock ? 'rgba(255,107,0,0.15)' : 'rgba(0,229,255,0.08)',
                border: `1px solid ${useMock ? '#ff6d0055' : '#00e5ff22'}`,
                borderRadius: 6,
                padding: '5px 12px',
                color: useMock ? '#ff9800' : '#8899aa',
                fontSize: 10,
                letterSpacing: 1.5,
                cursor: 'pointer',
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {useMock ? '◉ DEMO MODE' : '○ DEMO MODE'}
            </button>
            <div style={{ textAlign: 'right' }}>
              <StatusDot connected={connected || useMock} />
              <div style={{ fontSize: 10, color: '#334455', marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
                SESSION {sessionFmt}
              </div>
            </div>
          </div>
        </header>

        {/* ── Metric Cards ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 12,
          marginBottom: 20,
        }}>
          {Object.entries(CLASS_COLORS).map(([cls, color]) => (
            <MetricCard
              key={cls}
              label={CLASS_LABELS[cls]}
              value={counts[cls] || 0}
              color={color}
              unit="det"
            />
          ))}
        </div>

        {/* ── Main Grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 340px',
          gap: 16,
          marginBottom: 16,
        }}>
          {/* Left: video + trend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <VideoFeed frameUrl={frameUrl} connected={connected} />

            {/* Trend Chart */}
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(0,229,255,0.1)',
              borderRadius: 8,
              padding: '14px 16px',
            }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: '#8899aa', marginBottom: 12, textTransform: 'uppercase' }}>
                ↗ Vehicle Count — Rolling Trend
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="time" tick={{ fill: '#334455', fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: '#334455', fontSize: 9 }} width={28} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="total" stroke="#00e5ff" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="car" stroke={CLASS_COLORS.car} strokeWidth={1} dot={false} isAnimationActive={false} strokeOpacity={0.6} />
                  <Line type="monotone" dataKey="truck" stroke={CLASS_COLORS.truck} strokeWidth={1} dot={false} isAnimationActive={false} strokeOpacity={0.6} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right: summary stats + pie + bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Summary stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <MetricCard label="Total Now" value={totalNow} color="#00e5ff" icon="🚗" />
              <MetricCard label="Peak Total" value={peakTotal} color="#ffea00" icon="📈" />
              <MetricCard label="FPS" value={fps} color="#76ff03" icon="⚡" />
              <MetricCard label="Model" value="YOLOv8n" unit="" color="#d500f9" />
            </div>

            {/* Pie chart */}
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(0,229,255,0.1)',
              borderRadius: 8,
              padding: '14px 16px',
              flex: 1,
            }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: '#8899aa', marginBottom: 8, textTransform: 'uppercase' }}>
                ◎ Class Distribution
              </div>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%" cy="50%"
                      innerRadius={45} outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                      isAnimationActive={false}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} stroke="transparent" />
                      ))}
                    </Pie>
                    <Legend
                      iconType="circle" iconSize={7}
                      formatter={(v) => <span style={{ color: '#8899aa', fontSize: 10 }}>{v}</span>}
                    />
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: '#334455', fontSize: 11, textAlign: 'center', padding: '40px 0' }}>
                  No detections yet
                </div>
              )}
            </div>

            {/* Bar chart */}
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(0,229,255,0.1)',
              borderRadius: 8,
              padding: '14px 16px',
            }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: '#8899aa', marginBottom: 8, textTransform: 'uppercase' }}>
                ▬ Peak Traffic Indicator
              </div>
              <ResponsiveContainer width="100%" height={100}>
                <BarChart data={barData} barSize={18}>
                  <XAxis dataKey="name" tick={{ fill: '#445566', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip content={<CustomTooltip />} />
                  {barData.map((entry) => (
                    <Bar key={entry.name} dataKey="count" fill={entry.fill} isAnimationActive={false} radius={[3,3,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ── Event Log ── */}
        <div style={{ height: 180 }}>
          <EventLog events={events} />
        </div>

        {/* ── Footer ── */}
        <footer style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid rgba(0,229,255,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 9,
          color: '#2a3a4a',
          letterSpacing: 1.5,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <span>TRAFFIQ v1.0 · YOLOv8n · WebSocket localhost:8765</span>
          <span>BUILT WITH REACT + RECHARTS · COLAB T4 BACKEND</span>
        </footer>
      </div>
    </>
  );
}
